import { useApolloClient } from '@apollo/client';
import type { SearchResults } from 'algoliasearch-helper';
import type { SearchResponse } from 'algoliasearch-helper/types/algoliasearch';
import { differenceInSeconds } from 'date-fns';
import type { Hit } from 'instantsearch.js';
import { useEffect, useMemo, useState } from 'react';
import { useInstantSearch, usePagination } from 'react-instantsearch';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { facetedSearchCards } from '@sorare/core/src/hooks/search/useFacetedSearchCards';
import { useSearchClient } from '@sorare/core/src/hooks/search/useSearchClient';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';
import type { MarketplaceHit, StackProps } from '@sorare/core/src/lib/algolia';
import { SaleType, joinFiltersWithOr } from '@sorare/core/src/lib/algolia';
import type { Card } from '@sorare/core/src/lib/cards';
import { isType } from '@sorare/core/src/lib/gql';

import { useSortByPrice } from '#src/hooks/useSortByPrice.ts';

import { useFacetCounts } from './useFacetCounts.ts';
import { useStableHits } from './useStableHits.ts';

type HitsState = {
  loading: boolean;
  hits: MarketplaceHit[] | undefined;
  results: SearchResults<Hit<MarketplaceHit>> | undefined;
};

const defaultUseHitsValue: HitsState = {
  loading: true,
  hits: undefined,
  results: undefined,
};

const defaultUseSortAndFilterItemsValue = {
  loading: true,
  items: undefined,
};

const MAX_AUTO_NEXT_PAGE = 10;

const outdated = (endDate: Date, offset: number = 0) =>
  differenceInSeconds(Date.now(), endDate) > offset;

export const useHits = ({
  sport,
  stackableTypes,
}: {
  sport: Sport;
  stackableTypes: SaleType[] | undefined;
}) => {
  const client = useApolloClient();
  const { algoliaCardIndexes } = useConfigContext();
  const searchClient = useSearchClient();
  const { hits, results, loading } = useStableHits<MarketplaceHit>();
  const { currentUser } = useCurrentUserContext();
  const { indexUiState } = useInstantSearch();
  const isCurrentUser = indexUiState.configure?.filters?.includes(
    `user.id:${idFromObject(currentUser?.id)}`
  );

  const unstacked = useQueryString(SEARCH_PARAMS.UNSTACKED);
  const stacked = !!stackableTypes?.length && !unstacked;
  // Originally, we also had some logic dealing with the `Newly Listed` index in order to avoid displaying the
  // lowest price hit instead of the newly listed one as the stack representant but we don't think this is
  // what we want to do anymore.
  const needToFetchLowestPriceHits =
    stacked && stackableTypes?.includes(SaleType.SINGLE_SALE_OFFER);
  const [state, setState] = useState<HitsState>(defaultUseHitsValue);

  useEffect(() => {
    (async () => {
      if (!results || loading) {
        setState(defaultUseHitsValue);
        return;
      }

      // eslint-disable-next-line no-underscore-dangle
      const distinctHits = hits?.filter(h => !h._distinctSeqID) || [];

      const lowestPriceResponse =
        needToFetchLowestPriceHits && distinctHits.length > 0
          ? await facetedSearchCards<MarketplaceHit>(searchClient, {
              index: algoliaCardIndexes['Cards On Sale Lowest Price'],
              distinct: true,
              facetFilters: joinFiltersWithOr(
                distinctHits.flatMap((h, i) => [
                  `sale.distinct_key:"${h.sale?.distinct_key}"<score=${
                    1000 - i
                  }>`,
                  `objectID:"${h.objectID}"<score=${1000 - i}>`,
                ])
              ),
              // eslint-disable-next-line no-underscore-dangle
              params: results._state,
            })
          : undefined;

      const actualHits: MarketplaceHit[] =
        (lowestPriceResponse as SearchResponse<MarketplaceHit> | undefined)
          ?.hits || distinctHits;
      setState({
        loading,
        hits: actualHits,
        results,
      });
    })();
  }, [
    algoliaCardIndexes,
    client,
    hits,
    isCurrentUser,
    loading,
    needToFetchLowestPriceHits,
    results,
    searchClient,
    sport,
    stacked,
  ]);

  return state;
};

export const useSortAndFilterItems = <T extends Card>({
  hits,
  results,
  cardsBySlug,
  stackableTypes,
  removeFinishedAuctions,
  removeEndedSingleSaleOffers,
  removeFinishedPrimaryBuys,
  hideInstantBuy,
  sport,
}: {
  hits: MarketplaceHit[] | undefined;
  results: SearchResults<Hit<MarketplaceHit>> | undefined;
  cardsBySlug: Partial<Record<string, T[]>> | undefined;
  stackableTypes: SaleType[] | undefined;
  removeFinishedAuctions?: boolean;
  removeEndedSingleSaleOffers?: boolean;
  removeFinishedPrimaryBuys?: boolean;
  hideInstantBuy?: boolean;
  sport: Sport;
}) => {
  type Item = T & { stack?: StackProps };

  const { algoliaCardIndexes } = useConfigContext();
  const { refine: setPage } = usePagination();
  const sortByPrice = useSortByPrice<T>();
  const [state, setState] = useState<{
    loading: boolean;
    items: Item[] | undefined;
  }>(defaultUseSortAndFilterItemsValue);
  const unstacked = useQueryString(SEARCH_PARAMS.UNSTACKED);
  const stacked = !!stackableTypes?.length && !unstacked;

  const distinctKeys = useMemo(
    () => hits?.map(hit => hit.sale?.distinct_key).filter(Boolean),
    [hits]
  );
  const withStackCounts = stacked && distinctKeys && distinctKeys.length > 0;
  const stackedTokensCounts = useFacetCounts({
    index: algoliaCardIndexes['Cards On Sale Highest Price'],
    attribute: 'sale.distinct_key',
    values: distinctKeys,
    // eslint-disable-next-line no-underscore-dangle
    params: results?._state,
    skip: !withStackCounts,
    sport,
  });

  useEffect(() => {
    if (!hits || !cardsBySlug || (withStackCounts && !stackedTokensCounts)) {
      setState(s => (s.loading ? s : { ...s, loading: true }));
      return;
    }

    let sortedHits = hits;

    // Not all cards from the same player have the same popularity attribute indexed
    // so it's necessary to re-sort the hits by rarity and season manually so that all cards
    // from the same player are grouped together.
    if (
      [
        algoliaCardIndexes['Popular Player'],
        algoliaCardIndexes['Cards On Sale Popular Player'],
      ].includes(results?.index || '')
    ) {
      const tempSortedHits: MarketplaceHit[] = [];

      for (let i = 0; i < sortedHits.length; i += 1) {
        const { slug } = sortedHits[i].player || {};
        if (!slug) {
          tempSortedHits.push(sortedHits[i]);
        } else if (!tempSortedHits.find(h => h.player?.slug === slug)) {
          tempSortedHits.push(
            ...sortedHits
              .slice(i)
              .filter(h => h.player?.slug === slug)
              .sort(
                (a, b) =>
                  (a.rarity || '').localeCompare(b.rarity || '') ||
                  (b.season || 0) - (a.season || 0)
              )
          );
        }
      }

      sortedHits = tempSortedHits;
    }

    const cleanedSortedHits = stacked
      ? sortedHits.reduce<MarketplaceHit[]>((acc, hit) => {
          if (
            acc.some(
              h =>
                h.sale?.type &&
                stackableTypes?.includes(h.sale?.type as SaleType) &&
                h.sale?.distinct_key === hit.sale?.distinct_key
            )
          )
            return acc;
          return [...acc, hit];
        }, [])
      : sortedHits;

    const stackedItems = cleanedSortedHits
      .map(hit => {
        if (!cardsBySlug[hit.slug]) {
          // hit filtered out by GraphQL
          return undefined;
        }

        const item = cardsBySlug[hit.slug]![0]!;
        if (
          hit.sale?.type &&
          stackableTypes?.includes(hit.sale?.type as SaleType) &&
          (stackedTokensCounts || hit.distinctKeyHitCount) &&
          hit.sale?.distinct_key
        ) {
          return {
            stack: {
              algoliaDistinctKey: hit.sale.distinct_key,
              // eslint-disable-next-line no-underscore-dangle
              params: results?._state,
              count:
                hit.distinctKeyHitCount ||
                stackedTokensCounts[hit.sale.distinct_key],
            },
            ...item,
          };
        }

        return item;
      }, [])
      .filter(Boolean);

    const filteredItems = stackedItems.filter(item => {
      if (
        isType(item, 'Card') ||
        isType(item, 'NBACard') ||
        isType(item, 'BaseballCard')
      ) {
        const {
          latestEnglishAuction,
          liveSingleSaleOffer,
          livePrimaryOffer,
          user,
        } = item;
        if (
          !latestEnglishAuction &&
          !liveSingleSaleOffer &&
          !livePrimaryOffer &&
          !user
        ) {
          // the card has been created but it is not yet on sale do not show
          return false;
        }

        if (
          hideInstantBuy &&
          livePrimaryOffer &&
          livePrimaryOffer?.nfts?.length === 1 &&
          // WARNING: WE NEED TO EXPOSE THE PRIMARY OFFER STATUS IN THE API
          // TO RELY ON THE STATUS AND NOT THE AUCTION OBJECT
          !latestEnglishAuction?.open
        ) {
          return false;
        }

        if (
          removeFinishedPrimaryBuys &&
          livePrimaryOffer &&
          (outdated(livePrimaryOffer.endDate) || user)
        ) {
          if (stacked) {
            // do not remove it, let Token query Algolia+backend to get the next card to display
            return true;
          }
          return false;
        }

        // On secondary market, if a single sale offer has ended we shouldn't even display the hit
        // as otherwise we would show the owner and the date since when they own the card, not the new owner
        if (removeEndedSingleSaleOffers && !liveSingleSaleOffer) {
          if (stacked) {
            // do not remove it, let Token query Algolia+backend to get the next card to display
            return true;
          }
          return false;
        }

        // For new signings, if auction has been finished for more than 10 seconds, than remove from the list
        if (
          removeFinishedAuctions &&
          latestEnglishAuction &&
          !latestEnglishAuction.open
        ) {
          if (outdated(latestEnglishAuction.endDate, 10)) {
            return false;
          }
        }
      }
      return true;
    });

    // As we filter out the hits we receive from Algolia to handle indexing lags
    // we might be in a situation where there are no more hits to display.
    // This logic will go to the next page up to reaching page `MAX_AUTO_NEXT_PAGE` (safety)
    // until there are some hits left to display.
    if (
      filteredItems.length === 0 &&
      stackedItems.length !== 0 &&
      (removeFinishedAuctions || removeEndedSingleSaleOffers) &&
      results &&
      results.page < results.nbPages - 1 &&
      results.page < MAX_AUTO_NEXT_PAGE
    ) {
      setPage(results.page + 1);
      setState({ loading: true, items: [] });
      return;
    }

    const sortPriceDesc = [
      algoliaCardIndexes['Highest Price'],
      algoliaCardIndexes['Cards On Sale Highest Price'],
    ].includes(results?.index || '');
    const sortPriceAsc = [
      algoliaCardIndexes['Lowest Price'],
      algoliaCardIndexes['Cards On Sale Lowest Price'],
    ].includes(results?.index || '');
    const reSortedItems =
      sortPriceDesc || sortPriceAsc
        ? sortByPrice(filteredItems, sortPriceDesc ? 'desc' : 'asc')
            .map(t => filteredItems.find(i => t.slug === i.slug))
            .filter(Boolean)
        : filteredItems;

    setState({
      loading: false,
      items: reSortedItems,
    });
  }, [
    algoliaCardIndexes,
    cardsBySlug,
    hideInstantBuy,
    hits,
    results,
    removeEndedSingleSaleOffers,
    removeFinishedAuctions,
    removeFinishedPrimaryBuys,
    setPage,
    sortByPrice,
    stackableTypes,
    stacked,
    stackedTokensCounts,
    withStackCounts,
  ]);

  return state;
};
